function tokenizarRuta(ruta) {
  const tokens = []
  let actual = ''
  let i = 0

  const pushActual = () => {
    if (actual) {
      tokens.push({ tipo: 'key', valor: actual })
      actual = ''
    }
  }

  while (i < ruta.length) {
    const c = ruta[i]

    if (c === '.') {
      pushActual()
      i++
      continue
    }

    if (c === '[') {
      pushActual()
      i++
      let contenido = ''
      let enComillas = false
      let comilla = ''
      let escape = false

      while (i < ruta.length) {
        const ch = ruta[i]
        if (escape) {
          contenido += ch
          escape = false
          i++
          continue
        }
        if (enComillas) {
          if (ch === '\\') {
            escape = true
            i++
            continue
          }
          if (ch === comilla) {
            enComillas = false
            i++
            continue
          }
          contenido += ch
          i++
          continue
        }
        if (ch === '"' || ch === "'") {
          enComillas = true
          comilla = ch
          i++
          continue
        }
        if (ch === ']') {
          i++
          break
        }
        contenido += ch
        i++
      }

      const contenidoTrim = contenido.trim()
      if (contenidoTrim === '*') {
        tokens.push({ tipo: 'wildcard' })
      } else if (/^\d+$/.test(contenidoTrim)) {
        tokens.push({ tipo: 'index', valor: Number(contenidoTrim) })
      } else if (contenidoTrim) {
        tokens.push({ tipo: 'key', valor: contenidoTrim })
      }
      continue
    }

    actual += c
    i++
  }

  pushActual()
  return tokens
}

function flattenOneLevel(arr) {
  const out = []
  for (const item of arr) {
    if (Array.isArray(item)) out.push(...item)
    else out.push(item)
  }
  return out
}

function crearErrorRuta(code, message, ruta, segmento) {
  return {
    code,
    message,
    detail: {
      ruta,
      segmento,
    },
  }
}

function propiedadExiste(actual, propiedad) {
  return Object.prototype.hasOwnProperty.call(actual, propiedad)
}

export function resolverValorDetallado(estructura, ruta) {
  const rutaTexto = String(ruta ?? '').trim()
  if (!rutaTexto) {
    return {
      ok: false,
      error: crearErrorRuta('path_empty', 'Ruta vacía', rutaTexto, ''),
    }
  }

  const tokens = tokenizarRuta(rutaTexto)
  if (!tokens.length) {
    return {
      ok: false,
      error: crearErrorRuta('path_invalid', 'Ruta inválida', rutaTexto, rutaTexto),
    }
  }

  function resolver(actual, idx, recorrido) {
    if (idx >= tokens.length) {
      return { ok: true, value: actual ?? '' }
    }

    const token = tokens[idx]

    if (token.tipo === 'key') {
      if (actual == null || typeof actual !== 'object') {
        return {
          ok: true,
          value: '',
          missing: true,
        }
      }

      if (!propiedadExiste(actual, token.valor)) {
        return {
          ok: true,
          value: tokens.slice(idx + 1).some((item) => item.tipo === 'wildcard') ? [] : '',
          missing: true,
        }
      }

      const siguienteRecorrido = recorrido ? `${recorrido}.${token.valor}` : token.valor
      return resolver(actual[token.valor], idx + 1, siguienteRecorrido)
    }

    if (token.tipo === 'index') {
      if (!Array.isArray(actual)) {
        return {
          ok: true,
          value: '',
          missing: true,
        }
      }

      if (token.valor >= actual.length) {
        return {
          ok: true,
          value: '',
          missing: true,
        }
      }

      return resolver(actual[token.valor], idx + 1, `${recorrido}[${token.valor}]`)
    }

    if (token.tipo === 'wildcard') {
      if (!Array.isArray(actual)) {
        return {
          ok: true,
          value: [],
          missing: true,
        }
      }

      const resultados = []
      for (let i = 0; i < actual.length; i++) {
        const resuelto = resolver(actual[i], idx + 1, `${recorrido}[${i}]`)
        if (!resuelto.ok) return resuelto
        if (Array.isArray(resuelto.value)) resultados.push(...resuelto.value)
        else resultados.push(resuelto.value)
      }

      return { ok: true, value: resultados }
    }

    return {
      ok: false,
      error: crearErrorRuta('path_token_invalid', 'Token de ruta inválido', rutaTexto, recorrido),
    }
  }

  return resolver(estructura, 0, '')
}

export function resolverValorEstricto(estructura, ruta) {
  const resultado = resolverValorDetallado(estructura, ruta)
  if (!resultado.ok) {
    const error = new Error(resultado.error.message)
    error.code = resultado.error.code
    error.detail = resultado.error.detail
    throw error
  }
  return resultado.value
}

export function resolverValor(estructura, ruta) {
  if (!ruta) return ''
  const tokens = tokenizarRuta(ruta)

  function resolver(actual, idx) {
    if (idx >= tokens.length) return actual
    const token = tokens[idx]

    if (token.tipo === 'key') {
      if (actual == null || typeof actual !== 'object') return ''
      return resolver(actual[token.valor], idx + 1)
    }

    if (token.tipo === 'index') {
      if (!Array.isArray(actual)) return ''
      return resolver(actual[token.valor], idx + 1)
    }

    if (token.tipo === 'wildcard') {
      if (!Array.isArray(actual)) return ''
      const resultados = actual.map((item) => resolver(item, idx + 1))
      return flattenOneLevel(resultados)
    }

    return ''
  }

  const valor = resolver(estructura, 0)
  return valor ?? ''
}
